/**
 * vintageSfx — tiny Web-Audio synth that simulates the mechanical sounds
 * of an old turntable: a short electromechanical relay click and a
 * lower-pitched tonearm thump. Used by the RadioPlayer when starting
 * playback to give the vinyl UI a tactile, vintage feel.
 *
 * All sounds are synthesised on the fly — no assets to ship.
 *
 * Important: the AudioContext is created on demand and CLOSED right after
 * the last scheduled sound finishes. On iOS/CarPlay, keeping a parallel
 * Web Audio session open while an HTMLAudioElement is streaming can
 * disturb the system audio session (causing tiny stutters), so we let
 * the context go to sleep as soon as possible.
 */

function createCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    return new AC();
  } catch (_e) {
    return null;
  }
}

function closeCtxAfter(ctx, seconds) {
  if (!ctx) return;
  setTimeout(() => {
    try {
      if (ctx.state !== "closed" && typeof ctx.close === "function") {
        ctx.close().catch(() => {});
      }
    } catch (_e) {
      /* ignore */
    }
  }, Math.max(50, seconds * 1000));
}

function scheduleRelay(ctx, delay = 0) {
  const t0 = ctx.currentTime + delay;
  const len = Math.floor(ctx.sampleRate * 0.04);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  const decay = ctx.sampleRate * 0.006;
  for (let i = 0; i < len; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / decay);
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 2400;
  bp.Q.value = 4;
  const g = ctx.createGain();
  g.gain.value = 0.32;
  src.connect(bp);
  bp.connect(g);
  g.connect(ctx.destination);
  src.start(t0);
  return t0 + 0.05;
}

function scheduleTonearm(ctx, delay = 0) {
  const t0 = ctx.currentTime + delay;
  const len = Math.floor(ctx.sampleRate * 0.08);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  const decay = ctx.sampleRate * 0.012;
  for (let i = 0; i < len; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / decay);
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 750;
  const ng = ctx.createGain();
  ng.gain.value = 0.55;
  src.connect(lp);
  lp.connect(ng);
  ng.connect(ctx.destination);
  src.start(t0);

  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.value = 88;
  const og = ctx.createGain();
  og.gain.setValueAtTime(0.0001, t0);
  og.gain.exponentialRampToValueAtTime(0.28, t0 + 0.006);
  og.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
  osc.connect(og);
  og.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + 0.2);
  return t0 + 0.2;
}

/** Compound startup: relay snap + tonearm drop ~180ms later. Context closes ~0.6s after start. */
export function playStartupSequence() {
  const ctx = createCtx();
  if (!ctx) return;
  scheduleRelay(ctx, 0);
  const endsAt = scheduleTonearm(ctx, 0.18);
  closeCtxAfter(ctx, endsAt - ctx.currentTime + 0.2);
}

/** Soft single relay click for stop/pause. */
export function playStopClick() {
  const ctx = createCtx();
  if (!ctx) return;
  const endsAt = scheduleRelay(ctx, 0);
  closeCtxAfter(ctx, endsAt - ctx.currentTime + 0.2);
}
